import * as THREE from 'three';
import mapboxgl from 'mapbox-gl';
import { STYLES, type StyleName } from '../core/styles';

export class SceneSystem implements mapboxgl.CustomLayerInterface {
  id = 'three-layer';
  type = 'custom' as const;
  renderingMode = '3d' as const;

  readonly scene: THREE.Scene;
  readonly camera: THREE.Camera;
  map!: mapboxgl.Map;

  private renderer!: THREE.WebGLRenderer;
  private centerMerc: mapboxgl.MercatorCoordinate;
  private worldScale: number;
  private keyLight!: THREE.DirectionalLight;
  private fillLight!: THREE.DirectionalLight;
  private ambientLight!: THREE.AmbientLight;
  private hemiLight!: THREE.HemisphereLight;
  private grid!: THREE.GridHelper;
  private groundPlane!: THREE.Mesh;

  constructor(center: [number, number]) {
    this.scene = new THREE.Scene();
    this.camera = new THREE.Camera();
    this.centerMerc = mapboxgl.MercatorCoordinate.fromLngLat(center, 0);
    this.worldScale = this.centerMerc.meterInMercatorCoordinateUnits();
  }

  onAdd(map: mapboxgl.Map, gl: WebGLRenderingContext) {
    this.map = map;

    this.renderer = new THREE.WebGLRenderer({
      canvas: map.getCanvas(),
      context: gl,
      antialias: true,
    });
    this.renderer.autoClear = false;
    // Cinematic filmic grade for the Three layer (buildings/vehicles).
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.4;

    this.keyLight = new THREE.DirectionalLight(0x223355, 0.8);
    this.keyLight.position.set(-80, 300, -120);
    this.scene.add(this.keyLight);

    this.fillLight = new THREE.DirectionalLight(0x112244, 0.4);
    this.fillLight.position.set(100, 150, 80);
    this.scene.add(this.fillLight);

    this.ambientLight = new THREE.AmbientLight(0x111122, 0.5);
    this.scene.add(this.ambientLight);

    this.hemiLight = new THREE.HemisphereLight(0x111133, 0x000000, 0.3);
    this.scene.add(this.hemiLight);

    // Ground plane first (below everything)
    const groundGeo = new THREE.PlaneGeometry(14000, 14000);
    const groundMat = new THREE.MeshBasicMaterial({ color: 0x0e1118, depthWrite: false });
    this.groundPlane = new THREE.Mesh(groundGeo, groundMat);
    this.groundPlane.rotation.x = -Math.PI / 2;
    this.groundPlane.position.y = -0.5;
    this.groundPlane.renderOrder = -2;
    this.scene.add(this.groundPlane);

    // Grid on top of ground
    this.grid = new THREE.GridHelper(12000, 120, 0x44bbee, 0x3399cc);
    this.grid.position.y = -0.05;
    this.grid.renderOrder = -1;
    const mats = Array.isArray(this.grid.material) ? this.grid.material : [this.grid.material];
    for (const m of mats) {
      (m as THREE.LineBasicMaterial).transparent = true;
      (m as THREE.LineBasicMaterial).opacity = 0.6;
      (m as THREE.LineBasicMaterial).depthWrite = false;
    }
    this.scene.add(this.grid);
  }

  applyStyle(styleName: StyleName): void {
    const s = STYLES[styleName];

    this.keyLight.color.set(s.lighting.key.color);
    this.keyLight.intensity = s.lighting.key.intensity;
    this.fillLight.color.set(s.lighting.fill.color);
    this.fillLight.intensity = s.lighting.fill.intensity;
    this.ambientLight.color.set(s.lighting.ambient.color);
    this.ambientLight.intensity = s.lighting.ambient.intensity;
    this.hemiLight.color.set(s.lighting.hemi.sky);
    this.hemiLight.groundColor.set(s.lighting.hemi.ground);
    this.hemiLight.intensity = s.lighting.hemi.intensity;

    // Grid colors (GridHelper has [centerLine, divisions] materials)
    const gridMats = Array.isArray(this.grid.material) ? this.grid.material : [this.grid.material];
    if (gridMats.length >= 2) {
      (gridMats[0] as THREE.LineBasicMaterial).color.set(s.grid.majorColor);
      (gridMats[0] as THREE.LineBasicMaterial).opacity = s.grid.opacity;
      (gridMats[1] as THREE.LineBasicMaterial).color.set(s.grid.minorColor);
      (gridMats[1] as THREE.LineBasicMaterial).opacity = s.grid.opacity * 0.6;
    } else {
      (gridMats[0] as THREE.LineBasicMaterial).color.set(s.grid.minorColor);
      (gridMats[0] as THREE.LineBasicMaterial).opacity = s.grid.opacity;
    }

    // Ground plane color
    if (this.groundPlane) {
      const groundColor = styleName === 'dark' ? 0x0e1118 : 0xd8d8d8;
      (this.groundPlane.material as THREE.MeshBasicMaterial).color.set(groundColor);
    }

    // Mapbox background + fog + all hidden layers
    if (this.map) {
      const mapStyle = this.map.getStyle();
      if (mapStyle?.layers) {
        for (const layer of mapStyle.layers) {
          if (layer.type === 'background') {
            try { this.map.setPaintProperty(layer.id, 'background-color', s.background); } catch {}
          }
        }
      }
      this.map.setFog({
        color: s.fog.color,
        'high-color': s.fog.highColor,
        'horizon-blend': s.fog.blend,
        'space-color': s.fog.spaceColor,
        'star-intensity': s.fog.stars,
      });
      // Canvas background for areas not covered by the map style
      this.map.getCanvas().style.backgroundColor = s.background;
    }
  }

  render(_gl: WebGLRenderingContext, args: any) {
    let matrixData: any;
    if (args?.defaultProjectionData?.mainMatrix) {
      matrixData = args.defaultProjectionData.mainMatrix;
    } else if (ArrayBuffer.isView(args) || Array.isArray(args)) {
      matrixData = args;
    } else {
      return;
    }

    const modelMatrix = new THREE.Matrix4().makeTranslation(
      this.centerMerc.x,
      this.centerMerc.y,
      this.centerMerc.z as number
    );
    modelMatrix.multiply(
      new THREE.Matrix4().makeScale(this.worldScale, -this.worldScale, this.worldScale)
    );
    modelMatrix.multiply(new THREE.Matrix4().makeRotationX(Math.PI / 2));

    const projMatrix = new THREE.Matrix4().fromArray(matrixData as number[]);
    this.camera.projectionMatrix = projMatrix.multiply(modelMatrix);
    this.camera.projectionMatrixInverse.copy(this.camera.projectionMatrix).invert();

    this.renderer.resetState();
    this.renderer.render(this.scene, this.camera);
    this.map.triggerRepaint();
  }

  lngLatToWorld(lng: number, lat: number, altitude = 0): THREE.Vector3 {
    const merc = mapboxgl.MercatorCoordinate.fromLngLat([lng, lat], altitude);
    return new THREE.Vector3(
      (merc.x - this.centerMerc.x) / this.worldScale,
      altitude,
      (merc.y - this.centerMerc.y) / this.worldScale
    );
  }

  worldToLngLat(pos: THREE.Vector3): [number, number] {
    const mercX = pos.x * this.worldScale + this.centerMerc.x;
    const mercY = pos.z * this.worldScale + this.centerMerc.y;
    const coord = new mapboxgl.MercatorCoordinate(mercX, mercY, 0);
    const lngLat = coord.toLngLat();
    return [lngLat.lng, lngLat.lat];
  }

  add(object: THREE.Object3D): void {
    this.scene.add(object);
  }

  remove(object: THREE.Object3D): void {
    this.scene.remove(object);
  }
}
